<script>
import { AbcBadge } from "@ordbogen-npm/abc-dsk";
import { mapStores, storeToRefs } from "pinia";
import { RouterLink } from "vue-router";

import { BREAKPOINTS } from "@/common/config";
import { isIframe } from "@/common/is-iframe";
import { addPackageAndLangParams } from "@/common/route-path-helpers";
import WorkshopNav from "@/common/workshop-nav";
import DesktopMenuButton from "@/components/header/DesktopMenuButton.vue";
import HeaderLogo from "@/components/header/HeaderLogo.vue";
import LanguageSelectorButton from "@/components/header/LanguageSelectorButton.vue";
import MainNavbar from "@/components/header/MainNavbar.vue";
import MobileMenu from "@/components/header/MobileMenu.vue";
import MobileMenuButton from "@/components/header/MobileMenuButton.vue";
import WorkshopNavbar from "@/components/header/WorkshopNavbar.vue";
import IconWorkshop from "@/components/icons/green/IconGreenWorkshop.vue";
import IconInfo from "@/components/icons/IconInfo.vue";
import IconPen from "@/components/icons/IconPen.vue";
import IconVocab from "@/components/icons/IconVocab.vue";
import IconSearch from "@/components/icons/white/IconWhiteSearch.vue";
import UniloginButton from "@/components/UniloginButton.vue";
import { MatomoHelper } from "@/helpers";
import { useAuthStore, useMenuStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

export default {
  name: "TheHeader",
  components: {
    DesktopMenuButton,
    MobileMenuButton,
    MobileMenu,
    HeaderLogo,
    LanguageSelectorButton,
    MainNavbar,
    WorkshopNavbar,
    IconInfo,
    IconPen,
    IconSearch,
    IconWorkshop,
    IconVocab,
    UniloginButton,
    AbcBadge,
    RouterLink
  },
  data() {
    const authStore = useAuthStore();
    const { userIsTeacher } = storeToRefs(authStore);

    return {
      windowWidth: window.innerWidth,
      isWorkshopMenuOpen: false,
      lgBreakpoint: BREAKPOINTS.lg,
      userIsTeacher
    };
  },
  computed: {
    ...mapStores(useAuthStore, useMenuStore),

    showMobileMenu() {
      return !isIframe();
    }
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    isWorkshopNavVisible: WorkshopNav.isVisible,

    getWorkshopRoute() {
      return addPackageAndLangParams({ name: "workshop" });
    },

    showWorkshopMenu() {
      this.isWorkshopMenuOpen = true;
    },

    hideWorkshopMenu() {
      this.isWorkshopMenuOpen = false;
    },

    onResize() {
      this.windowWidth = window.innerWidth;
    },

    trackWordhuntClick() {
      MatomoHelper.sendEvent(MatomoEventCategory.AbcWordhunt, MatomoEventAction.ClickWordhunt);
    },

    trackExamInfoClick() {
      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcExamPage,
        MatomoEventAction.ClickExamInfoButton,
        "Clicked topbar exam info"
      );
    },

    trackWorksheetsClick() {
      MatomoHelper.sendEvent(
        MatomoEventCategory.Worksheets,
        MatomoEventAction.ClickWorksheetsButton,
        "Clicked topbar worksheets"
      );
    }
  }
};
</script>

<template>
  <header
    class="header"
    :class="{ 'header--fixed': menuStore.isMenuOpen && windowWidth < lgBreakpoint }"
  >
    <template v-if="authStore.isAuthenticated">
      <div class="container-lg">
        <div class="header__group row">
          <!-- Mobile menu -->
          <div
            v-if="showMobileMenu"
            class="header__group-container col-2 col-sm p-0 d-flex d-lg-none align-items-center position-static"
          >
            <MobileMenuButton
              id="mobile-menu-button"
              :expanded="menuStore.isMenuOpen"
              :aria-label="$t('menu.label')"
              aria-controls="main-navbar-mobile"
              @click.stop="menuStore.toggleMenu()"
            />
            <Transition name="slide-fade" :duration="{ enter: 100, leave: 100 }">
              <MobileMenu
                v-if="menuStore.isMenuOpen"
                id="main-navbar-mobile"
                trigger-id="mobile-menu-button"
              />
            </Transition>
          </div>
          <!-- Desktop header logo -->
          <div
            class="header__logo-logged-in-container justify-content-sm-center justify-content-lg-start"
            :class="{ 'ml-2': !showMobileMenu }"
          >
            <HeaderLogo />
          </div>
          <!-- Desktop navigation links -->
          <nav class="header__group-container d-none d-lg-flex align-items-center position-static">
            <ul class="header__item-group list-unstyled m-0 d-flex">
              <li>
                <RouterLink class="header__item" :to="{ name: 'dictionary' }">
                  <IconSearch class="header__icon" height="15" width="15" aria-hidden="true" />
                  <span>{{ $t("menu.dictionary") }}</span>
                </RouterLink>
              </li>
              <li v-if="isWorkshopNavVisible()">
                <div
                  @mouseover="showWorkshopMenu()"
                  @mouseleave="hideWorkshopMenu()"
                  @blur="hideWorkshopMenu()"
                >
                  <RouterLink
                    id="workshop-menu"
                    class="header__item"
                    :to="getWorkshopRoute()"
                    @focus="showWorkshopMenu()"
                  >
                    <IconWorkshop class="header__icon" height="23" width="23" aria-hidden="true" />
                    <span>{{ $t("menu.workshop") }}</span>
                  </RouterLink>
                  <Transition name="slide-fade" :duration="{ enter: 100, leave: 100 }">
                    <WorkshopNavbar
                      v-if="isWorkshopMenuOpen"
                      id="workshop-navbar"
                      trigger-id="workshop-menu"
                      @close="hideWorkshopMenu()"
                    />
                  </Transition>
                </div>
              </li>
              <li>
                <RouterLink
                  class="header__item"
                  :to="{ name: 'vocab.home' }"
                  @click="trackWordhuntClick()"
                >
                  <IconVocab class="header__icon" aria-hidden="true" />
                  <span>{{ $t("menu.vocab") }}</span>
                  <AbcBadge class="header__badge d-none" color="secondary">{{
                    $t("menu.badge")
                  }}</AbcBadge>
                </RouterLink>
              </li>
              <li v-if="userIsTeacher">
                <RouterLink
                  class="header__item"
                  :to="{ name: 'opgavehaefter' }"
                  @click="trackWorksheetsClick()"
                >
                  <IconPen class="header__icon" aria-hidden="true" />
                  <span>{{ $t("menu.items.worksheets") }}</span>
                  <AbcBadge class="header__badge" color="secondary">{{
                    $t("menu.badge")
                  }}</AbcBadge>
                </RouterLink>
              </li>
              <li v-if="false">
                <RouterLink
                  class="header__item"
                  :to="{ name: 'exam-preparation' }"
                  @click="trackExamInfoClick()"
                >
                  <IconInfo class="header__icon" aria-hidden="true" />
                  <span>{{ $t("menu.exam-info") }}</span>
                </RouterLink>
              </li>
            </ul>
          </nav>
          <!-- Rest of the menu -->
          <div class="header__end-container position-static">
            <div
              class="header__group-container language-selector-container header__item-group d-flex align-items-center justify-content-end"
            >
              <div class="header__item">
                <LanguageSelectorButton />
              </div>
              <div class="d-none d-lg-block">
                <div class="header__desktop-menu-container">
                  <DesktopMenuButton
                    id="desktop-menu-button"
                    :expanded="menuStore.isMenuOpen"
                    :aria-label="$t('menu.label')"
                    aria-controls="main-navbar-desktop"
                    @click.stop="menuStore.toggleMenu()"
                    @keyup.down.stop="menuStore.toggleMenu()"
                  ></DesktopMenuButton>
                </div>
                <Transition name="slide-fade" :duration="{ enter: 100, leave: 100 }">
                  <MainNavbar
                    v-if="menuStore.isMenuOpen && windowWidth >= lgBreakpoint"
                    id="main-navbar-desktop"
                    trigger-id="desktop-menu-button"
                  />
                </Transition>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="container">
        <div class="header__group row mx-0">
          <span class="header__logo-container">
            <HeaderLogo />
          </span>
          <span class="header__cta-container">
            <RouterLink :to="{ name: 'get-access-today' }" class="header__cta-button">
              {{ $t("about.getAccessText") }}
            </RouterLink>
            <UniloginButton
              class="header__cta-button header__cta-button--bg-primary d-none d-md-inline-flex"
            >
              {{ $t("about.ctaBtnText") }}
            </UniloginButton>
          </span>
        </div>
      </div>
    </template>
  </header>
</template>
